import React from "react";
import Portal from "../../utils/portal";
import { MarkdownPageContext } from "@utils/MarkdownUtils";
import { ModalBtn } from "./ModalBtn";
import { graphql } from "gatsby";
import { DeepNonNullable } from "ts-essentials";
import { useModal } from "./ModalContext";
import { AccAnchor } from "@components/base/AccAnchor";

export const query = graphql`
  query SignupErrorModal($locale: String!) {
    strapiSignupErrorModal(locale: { eq: $locale }) {
      signIn
      contactSupport
      tryAgain
      ModalError {
        Code
        title
        content {
          data {
            content
          }
        }
      }
    }
  }
`;

const SignupErrorModal: React.FC<{
  data: DeepNonNullable<Queries.SignupErrorModalQuery>;
  sliceContext: {
    locale: string;
  };
}> = ({ data }) => {
  const {
    isSignupErrorModalOpen,
    signupErrorModalType,
    signupErrorModalMessage,
    hideSignupErrorModal,
  } = useModal();

  const { signIn, contactSupport, tryAgain, ModalError } =
    data.strapiSignupErrorModal;
  // set default values for errors
  const errors: {
    [key: string]: {
      title: string;
      content: string;
      showSignIn?: boolean;
      intercomMessage?: (email?: string) => string;
    };
  } = {
    MODAL_ERROR: {
      title: "Error creating trial",
      content: signupErrorModalMessage || "Something went wrong",
      showSignIn: false,
      intercomMessage: (email = "") =>
        `Hi\nI am trying to sign up for a trial with the email ${email} but I am told: ${
          signupErrorModalMessage || "Something went wrong"
        }.`,
    },
    ALREADY_EXIST_ERROR: {
      title: "Email address is already registered",
      content:
        "Sign in to AccuRanker, or try signing in again using a different email.",
      showSignIn: true,
    },
    WORK_EMAIL_ERROR: {
      title: "Only work email addresses are allowed",
      content: "Creating an AccuRanker trial account requires a work email.",
      showSignIn: false,
      intercomMessage: (email = "") =>
        `Hi\nI am trying to sign up for a trial with the email ${email} but I am told that free email addresses are not allowed.`,
    },
  };

  // Go through every error type and assing parameters with fallback
  ModalError.forEach(err => {
    errors[err.Code] = errors[err.Code] || {};

    errors[err.Code].title =
      err.title || errors[err.Code].title || errors.MODAL_ERROR.title;
    // Use message if it exists
    if (err.Code === "MODAL_ERROR" && signupErrorModalMessage) {
      errors[err.Code].content = signupErrorModalMessage;
    } else {
      // Use translated content if it exists if not use fallback content
      errors[err.Code].content =
        err.content.data.content ||
        errors[err.Code].content ||
        errors.MODAL_ERROR.content;
    }
  });

  // set modal data to render. Use MODAL_ERROR as a fallback
  const {
    title,
    content,
    showSignIn = false,
    intercomMessage,
  } = signupErrorModalType && errors[signupErrorModalType]
    ? errors[signupErrorModalType]
    : errors.MODAL_ERROR;
  if (!isSignupErrorModalOpen) return null;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto px-4 pt-4 sm:py-8"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="fixed inset-0 bg-gray-300 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={hideSignupErrorModal}
        />

        <div className="relative flex w-full max-w-md transform flex-col items-center overflow-hidden rounded-lg bg-accuRankerPurple-300 shadow-xl transition-all sm:max-w-xl">
          <div
            className="absolute right-2 top-1 z-10 cursor-pointer text-2xl text-gray-400"
            onClick={hideSignupErrorModal}
          >
            &#10006;
          </div>
          <div className="relative flex w-full flex-col items-stretch justify-start px-4 pt-8 pb-5 sm:px-8">
            <p className="text-2xl text-white">{title}</p>
            <div className="mt-2 flex flex-col text-sm text-gray-200">
              <MarkdownPageContext>{content}</MarkdownPageContext>
            </div>
            <div className="mt-8 flex justify-between gap-4">
              {showSignIn ? (
                <AccAnchor
                  href="https://app.accuranker.com/app/login"
                  rel="nofollow"
                >
                  <ModalBtn>{signIn}</ModalBtn>
                </AccAnchor>
              ) : (
                typeof intercomMessage === "function" && (
                  <ModalBtn
                    onClick={() =>
                      window.Intercom?.(
                        "showNewMessage",
                        intercomMessage(
                          document.querySelector<HTMLInputElement>("#email")
                            ?.value
                        )
                      )
                    }
                  >
                    {contactSupport}
                  </ModalBtn>
                )
              )}
              <ModalBtn color="white" onClick={hideSignupErrorModal}>
                {tryAgain}
              </ModalBtn>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default SignupErrorModal;
